import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { usePermission } from '../../hooks/usePermission';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { useToast } from '../../components/ui/Toast';
import { money } from '../../utils/format';
import { Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import {
  Drawer,
  Field,
  FieldRow,
  GstinInput,
  NumberInput,
  PanInput,
  PhoneInput,
  Select,
  TextInput,
} from '../../components/ui/Form';
import { CustomerDrawer } from './CustomerDrawer';

interface Customer {
  id: string;
  code: string;
  name: string;
  type: string;
  phone: string | null;
  gstin: string | null;
  credit_limit: string;
  outstanding?: string;
  advance_held?: string;
}

/** The states and union territories, for place of supply. */
const STATES = [
  'Tamil Nadu',
  'Andhra Pradesh',
  'Karnataka',
  'Kerala',
  'Telangana',
  'Puducherry',
  'Maharashtra',
  'Gujarat',
  'Rajasthan',
  'Delhi',
  'Uttar Pradesh',
  'Madhya Pradesh',
  'West Bengal',
  'Bihar',
  'Odisha',
  'Jharkhand',
  'Punjab',
  'Haryana',
  'Assam',
  'Chhattisgarh',
  'Goa',
  'Himachal Pradesh',
  'Jammu and Kashmir',
  'Uttarakhand',
  'Andaman and Nicobar Islands',
  'Chandigarh',
  'Dadra and Nagar Haveli and Daman and Diu',
  'Ladakh',
  'Lakshadweep',
  'Manipur',
  'Meghalaya',
  'Mizoram',
  'Nagaland',
  'Sikkim',
  'Tripura',
  'Arunachal Pradesh',
];

const BLANK = {
  code: '',
  name: '',
  type: 'RETAIL',
  phone: '',
  email: '',
  gstin: '',
  pan: '',
  line1: '',
  line2: '',
  city: '',
  state: 'Tamil Nadu',
  pincode: '',
  placeOfSupply: 'Tamil Nadu',
  creditLimit: '',
  creditDays: '',
  openingBalance: '',
  openingType: 'DR',
};

/**
 * Customers.
 *
 * The form used to ask for four things and the table could hold twelve, which
 * meant a shop had no way to record an address — needed on any invoice over
 * fifty thousand rupees, and most of these are — or a place of supply, which is
 * what decides whether their tax is CGST and SGST or IGST.
 *
 * It is laid out the way somebody fills it in: who they are, how to reach them,
 * what the taxman needs, and only then the terms. Everything past the first
 * section is optional, and says so, because a walk-in buying a chain should not
 * be held up by a form.
 */
export function CustomersPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [viewing, setViewing] = useState<string | null>(null);
  const [form, setForm] = useState({ ...BLANK });
  const [error, setError] = useState<unknown>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['customers', search],
    queryFn: () => endpoints.parties.customers(search || undefined),
  });

  const set = (patch: Partial<typeof BLANK>): void => setForm({ ...form, ...patch });

  const create = useMutation({
    mutationFn: () =>
      api.post<{ code: string; name: string }>('/customers', {
        ...(form.code.trim() ? { code: form.code } : {}),
        name: form.name,
        type: form.type,
        ...(form.phone ? { phone: form.phone } : {}),
        ...(form.email ? { email: form.email } : {}),
        ...(form.gstin ? { gstin: form.gstin } : {}),
        ...(form.pan ? { pan: form.pan } : {}),
        address: {
          ...(form.line1 ? { line1: form.line1 } : {}),
          ...(form.line2 ? { line2: form.line2 } : {}),
          ...(form.city ? { city: form.city } : {}),
          ...(form.state ? { state: form.state } : {}),
          ...(form.pincode ? { pincode: form.pincode } : {}),
        },
        ...(form.placeOfSupply ? { placeOfSupply: form.placeOfSupply } : {}),
        ...(form.creditLimit ? { creditLimit: form.creditLimit } : {}),
        ...(form.creditDays ? { creditDays: Number(form.creditDays) } : {}),
        ...(form.openingBalance ? { openingBalance: form.openingBalance, openingType: form.openingType } : {}),
      }),
    onSuccess: (created) => {
      toast(`${created.name} added`, `Customer ${created.code}`);
      setOpen(false);
      setForm({ ...BLANK });
      setError(null);
      void client.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: setError,
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    create.mutate();
  };

  const rows = (data?.data ?? []) as unknown as Customer[];
  // Only a name and a code are ever required: a walk-in buying a chain should
  // not be held at the counter while somebody finds their PAN.
  const ready = form.name.trim() !== '';

  return (
    <>
      <PageHeader
        title="Customers"
        subtitle={rows.length > 0 ? `${rows.length} on the books` : undefined}
        actions={
          <>
            <input
              className="field w-64"
              placeholder="Search name or phone"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {can('customer.create') ? (
              <button className="btn-primary whitespace-nowrap" onClick={() => setOpen(true)}>
                Add customer
              </button>
            ) : null}
          </>
        }
      />
      <ErrorNotice error={error} />

      {isLoading ? <Loading /> : null}
      {!isLoading && rows.length === 0 ? (
        <Empty
          message={search ? 'Nobody matches that search.' : 'No customers yet.'}
          hint="A walk-in needs no record at all — add somebody here when you want their history, their credit terms, or an address on the invoice."
          action={can('customer.create') ? { label: 'Add customer', onClick: () => setOpen(true) } : undefined}
        />
      ) : null}

      {rows.length > 0 ? (
        <Table head={['Code', 'Name', 'Type', 'Phone', 'GSTIN', 'Credit limit', 'Owes now', 'Advance held']}>
          {rows.map((c) => (
            <tr key={c.id} className="cursor-pointer hover:bg-gold-50" onClick={() => setViewing(c.id)}>
              <td className="td num">{c.code}</td>
              <td className="td font-medium">{c.name}</td>
              <td className="td text-2xs">{(c.type ?? '').toLowerCase() || '—'}</td>
              <td className="td num">{c.phone ? `+91 ${c.phone}` : '—'}</td>
              <td className="td num text-2xs">{c.gstin ?? '—'}</td>
              <td className="td num text-right">
                {Number(c.credit_limit) > 0 ? money(c.credit_limit) : <span className="text-ink-faint">cash only</span>}
              </td>
              <td className="td num text-right">
                {Number(c.outstanding ?? 0) > 0 ? (
                  <span className="text-danger">{money(c.outstanding)}</span>
                ) : Number(c.outstanding ?? 0) < 0 ? (
                  <span className="text-success" title="The shop owes this customer">
                    {money(Math.abs(Number(c.outstanding)).toFixed(2))} credit
                  </span>
                ) : (
                  <span className="text-ink-faint">—</span>
                )}
              </td>
              <td className="td num text-right">
                {Number(c.advance_held ?? 0) > 0 ? (
                  <span className="text-success" title="Paid ahead; the till offers it as “Use advance”">
                    {money(c.advance_held!)}
                  </span>
                ) : (
                  <span className="text-ink-faint">—</span>
                )}
              </td>
            </tr>
          ))}
        </Table>
      ) : null}

      {viewing ? <CustomerDrawer customerId={viewing} onClose={() => setViewing(null)} /> : null}

      {open ? (
        <Drawer
          title="Add a customer"
          subtitle="Only a code and a name are needed. The rest matters when you invoice them."
          onClose={() => setOpen(false)}
          footer={
            <div className="flex items-center gap-3">
              <button className="btn-primary" disabled={!ready || create.isPending} onClick={submit}>
                {create.isPending ? 'Saving…' : 'Save customer'}
              </button>
              <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>
                Cancel
              </button>
            </div>
          }
        >
          <ErrorNotice error={error} />

          <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-5">
            <section>
              <h3 className="mb-2 text-sm font-semibold">Who they are</h3>
              <FieldRow>
                <Field label="Code" hint="Left blank, a number like C0001 is assigned.">
                  <TextInput
                    value={form.code}
                    onChange={(v) => set({ code: v })}
                    placeholder="auto"
                    maxLength={32}
                    uppercase
                    allow={/[A-Z0-9\-_/]/}
                  />
                </Field>
                <Field label="Type" required>
                  <Select
                    value={form.type}
                    onChange={(v) => set({ type: v })}
                    options={[
                      { value: 'RETAIL', label: 'Retail' },
                      { value: 'WHOLESALE', label: 'Wholesale' },
                      { value: 'WALKIN', label: 'Walk-in' },
                    ]}
                  />
                </Field>
                <Field label="Name" required wide>
                  <TextInput
                    value={form.name}
                    onChange={(v) => set({ name: v })}
                    maxLength={200}
                    placeholder="Meena Ravi"
                    autoFocus
                  />
                </Field>
              </FieldRow>
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold">How to reach them</h3>
              <FieldRow>
                <Field label="Mobile">
                  <PhoneInput value={form.phone} onChange={(v) => set({ phone: v })} />
                </Field>
                <Field label="Email">
                  <TextInput
                    value={form.email}
                    onChange={(v) => set({ email: v })}
                    maxLength={200}
                    placeholder="name@example.com"
                  />
                </Field>
              </FieldRow>
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold">Address</h3>
              <p className="mb-2 text-2xs text-ink-faint">Needed on any invoice above ₹50,000.</p>
              <FieldRow>
                <Field label="Street" wide>
                  <TextInput value={form.line1} onChange={(v) => set({ line1: v })} maxLength={200} />
                </Field>
                <Field label="Area">
                  <TextInput value={form.line2} onChange={(v) => set({ line2: v })} maxLength={200} />
                </Field>
                <Field label="Town or city">
                  <TextInput value={form.city} onChange={(v) => set({ city: v })} maxLength={100} />
                </Field>
                <Field label="State">
                  <Select
                    value={form.state}
                    onChange={(v) => set({ state: v, placeOfSupply: v })}
                    options={STATES.map((s) => ({ value: s, label: s }))}
                  />
                </Field>
                <Field label="Pincode">
                  <TextInput value={form.pincode} onChange={(v) => set({ pincode: v })} maxLength={6} allow={/[0-9]/} />
                </Field>
              </FieldRow>
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold">Tax</h3>
              <FieldRow>
                <Field
                  wide
                  label="Place of supply"
                  hint="Decides whether their invoices carry CGST and SGST or IGST. Follows the state above unless you change it."
                >
                  <Select
                    value={form.placeOfSupply}
                    onChange={(v) => set({ placeOfSupply: v })}
                    options={STATES.map((s) => ({ value: s, label: s }))}
                  />
                </Field>
                <Field label="GSTIN" hint="Only if they are registered.">
                  <GstinInput value={form.gstin} onChange={(v) => set({ gstin: v })} />
                </Field>
                <Field label="PAN" hint="Asked for on cash purchases above ₹2 lakh.">
                  <PanInput value={form.pan} onChange={(v) => set({ pan: v })} />
                </Field>
              </FieldRow>
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold">Terms</h3>
              <FieldRow>
                <Field label="Credit limit" hint="Leave blank for cash only.">
                  <NumberInput
                    value={form.creditLimit}
                    onChange={(v) => set({ creditLimit: v })}
                    decimals={2}
                    suffix="₹"
                  />
                </Field>
                <Field label="Days to pay">
                  <NumberInput
                    value={form.creditDays}
                    onChange={(v) => set({ creditDays: v })}
                    decimals={0}
                    max={365}
                    suffix="days"
                  />
                </Field>
              </FieldRow>
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold">Opening balance</h3>
              <p className="mb-2 text-2xs text-ink-faint">
                Only when they already owed you something before this system. It posts to the ledger, so the books and
                this record cannot disagree.
              </p>
              <FieldRow>
                <Field label="Amount">
                  <NumberInput
                    value={form.openingBalance}
                    onChange={(v) => set({ openingBalance: v })}
                    decimals={2}
                    suffix="₹"
                  />
                </Field>
                <Field label="Which way">
                  <Select
                    value={form.openingType}
                    onChange={(v) => set({ openingType: v })}
                    options={[
                      { value: 'DR', label: 'They owe the shop' },
                      { value: 'CR', label: 'The shop holds their advance' },
                    ]}
                  />
                </Field>
              </FieldRow>
            </section>
          </form>
        </Drawer>
      ) : null}
    </>
  );
}
